// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import {
  DndContext,
  TouchSensor,
  useSensor,
  useSensors,
  rectIntersection,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
  CollisionDetection,
  PointerSensor,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Loader from "src/components/shared/loaders/Loader/Loader";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import SelectionCard from "./SelectionCard/SelectionCard";
import FilterSidebar from "./FilterSidebar/FilterSidebar";
import FilterDrawer from "./FilterSidebar/FilterDrawer/FilterDrawer";
import TimelineSidebar from "./TimelineSidebar/TimelineSidebar";
import TimelineDrawer from "./TimelineSidebar/TimelineDrawer/TimelineDrawer";
import SortHeader from "./SortHeader/SortHeader";
import { ChevronDownIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Utils
import { getFormattedDisplayDate } from "src/utils/date/dateHelper";

// Contexts
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

type SelectionScreenType = {
  selectedExamIds: Set<string>;
  onToggleExamSelection: (examId: string) => void;
  onToggleDateSelection: (
    examIds: Array<string>,
    shouldSelectAll: boolean
  ) => void;
  onEnterComparisonScreen: () => void;
};

const SelectionScreen = ({
  selectedExamIds,
  onToggleExamSelection,
  onToggleDateSelection,
  onEnterComparisonScreen,
}: SelectionScreenType) => {
  const { t } = useTranslation();
  const {
    selectedPatient,
    hasNoExternalExams,
    isLoading,
    languageCode,
    sortOptions,
    effectiveSortBy,
    sortDirection,
    filteredCards,
    dateGroups,
    examTypeFilter,
    examDateFilter,
    examEyeFilter,
    actions: { handleSortByClick, reorderCards },
  } = useExternalExamsContext();

  const theme = useTheme();
  const smallerThanMd = useMediaQuery(theme.breakpoints.down("md"));

  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set());

  const toggleDateCollapse = (dateKey: string) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [isTimelineCollapsed, setIsTimelineCollapsed] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isTimelineDrawerOpen, setIsTimelineDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor)
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
  }

  // Each date group has its own DndContext/SortableContext, so active/over
  // are always within the same date group here.
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    reorderCards(String(active.id), String(over.id));

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const fixCursorSnapOffset: CollisionDetection = (args) => {
    // Return if keyboard activated
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      // The collision rectangle is broken when using snapCenterToCursor
      // Reset the collision rectangle based on pointer location and overlay size
      collisionRect: {
        width,
        height,
        bottom: y + height / 2,
        left: x - width / 2,
        right: x + width / 2,
        top: y - height / 2,
      },
    };
    return rectIntersection(updated);
  };

  useEffect(() => {
    if (smallerThanMd) {
      setIsFiltersCollapsed(true);
      setIsTimelineCollapsed(true);
    } else {
      setIsFiltersCollapsed(false);
      setIsTimelineCollapsed(false);
      // Close mobile drawers on larger screens
      setIsFilterDrawerOpen(false);
      setIsTimelineDrawerOpen(false);
    }
  }, [smallerThanMd]);

  // On mobile, open drawers instead of sidebars
  const handleToggleFilters = useCallback(() => {
    if (smallerThanMd) {
      setIsFilterDrawerOpen(true);
    } else {
      setIsFiltersCollapsed((prev) => !prev);
    }
  }, [smallerThanMd]);

  const handleToggleTimeline = useCallback(() => {
    if (smallerThanMd) {
      setIsTimelineDrawerOpen(true);
    } else {
      setIsTimelineCollapsed((prev) => !prev);
    }
  }, [smallerThanMd]);

  return (
    <>
      {/* Main content row: desktop sidebar + scrollable cards */}
      <Grid
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: isFiltersCollapsed ? "50px" : "250px",
            flexShrink: 0,
            overflow: "hidden",
            borderRight:
              theme.palette.mode === "dark"
                ? "1px solid #5B6D7A"
                : "1px solid #5B6D7A50",
            transition: "width 0.2s ease",
          }}
        >
          <FilterSidebar
            hasData={!hasNoExternalExams}
            isCollapsed={isFiltersCollapsed}
            onToggleCollapse={handleToggleFilters}
            examTypeFilter={examTypeFilter}
            examDateFilter={examDateFilter}
            examEyeFilter={examEyeFilter}
          />
        </Box>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <SortHeader
            smallerThanMd={smallerThanMd}
            sortOptions={sortOptions}
            effectiveSortBy={effectiveSortBy}
            sortDirection={sortDirection}
            onSortByClick={handleSortByClick}
            selectedCount={selectedExamIds?.size ?? 0}
          />
          {isLoading && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Loader size="60px" containerProps={{ height: "60px" }} />
            </Box>
          )}
          {!isLoading &&
            (hasNoExternalExams || filteredCards?.length === 0) && (
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography fontWeight={600}>{t("No Exams Found.")}</Typography>
              </Box>
            )}
          {dateGroups.length > 0 && (
            <Box sx={{ flex: 1, minWidth: 0, p: "16px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {dateGroups.map(([dateKey, exams]) => {
                  const examIds = exams.map((exam) => exam.id);
                  const selectedCount = examIds.filter((examId) =>
                    selectedExamIds.has(examId)
                  ).length;
                  const isAllSelected =
                    examIds.length > 0 && selectedCount === examIds.length;
                  const isSomeSelected = selectedCount > 0 && !isAllSelected;

                  return (
                    <Accordion
                      key={dateKey}
                      expanded={!collapsedDates.has(dateKey)}
                      onChange={() => toggleDateCollapse(dateKey)}
                      disableGutters
                      sx={{
                        boxShadow: "none",
                        backgroundColor: "transparent",
                        backgroundImage: "none",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ChevronDownIcon style={{ color: "#FFF" }} />
                        }
                        sx={{
                          backgroundColor: "#202338",
                          borderRadius: "4px",
                          p: "8px",
                          minHeight: "auto",
                          "&.Mui-expanded": { minHeight: "auto" },
                          "& .MuiAccordionSummary-content": {
                            margin: 0,
                            "&.Mui-expanded": { margin: 0 },
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "8px",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <CustomCheckbox
                            id={`select-date-checkbox-${dateKey}`}
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            checkedFill="#E2772E"
                            checkedStroke="#FFF"
                            uncheckedFill="transparent"
                            uncheckedStroke="#FFF"
                            onClick={(event) => {
                              event.stopPropagation();
                              const isChecked = (
                                event.target as HTMLInputElement
                              ).checked;
                              onToggleDateSelection(examIds, isChecked);
                            }}
                            sx={{ mr: "8px" }}
                          />
                          <Typography
                            fontSize="14px"
                            fontWeight={600}
                            sx={{ color: "#FFF" }}
                          >
                            {getFormattedDisplayDate(dateKey, languageCode)}
                          </Typography>
                        </Box>

                        <Typography fontSize="12px" color="#FFF" mr="8px">
                          {exams.length} {t("exam", { count: exams.length })}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          p: 0,
                          pt: "16px",
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: "8px",
                        }}
                      >
                        <DndContext
                          sensors={sensors}
                          collisionDetection={fixCursorSnapOffset}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onDragCancel={handleDragCancel}
                        >
                          <SortableContext
                            items={examIds}
                            strategy={rectSortingStrategy}
                          >
                            {exams.map((exam) => (
                              <SelectionCard
                                key={exam.id}
                                isDragMode={activeId === exam.id}
                                selectedExam={exam}
                                isFavorited={exam.isFavorite}
                                isSelected={selectedExamIds.has(exam.id)}
                                onToggleSelect={() =>
                                  onToggleExamSelection(exam.id)
                                }
                                onEnterComparisonScreen={
                                  onEnterComparisonScreen
                                }
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          )}
        </Grid>
        <Box
          sx={{
            display: "flex",
            width: isTimelineCollapsed ? "50px" : "250px",
            flexShrink: 0,
            overflow: "hidden",
            borderLeft:
              theme.palette.mode === "dark"
                ? "1px solid #5B6D7A"
                : "1px solid #5B6D7A50",
            transition: "width 0.2s ease",
          }}
        >
          <TimelineSidebar
            selectedPatient={selectedPatient}
            isCollapsed={isTimelineCollapsed}
            onToggleCollapse={handleToggleTimeline}
          />
        </Box>
      </Grid>

      {/* Mobile-only: same sidebar content, shown as a drawer instead of expanding inline */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        hasData={!hasNoExternalExams}
        examTypeFilter={examTypeFilter}
        examDateFilter={examDateFilter}
        examEyeFilter={examEyeFilter}
      />
      <TimelineDrawer
        isOpen={isTimelineDrawerOpen}
        onClose={() => setIsTimelineDrawerOpen(false)}
        selectedPatient={selectedPatient}
      />
    </>
  );
};

export default SelectionScreen;
